import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/ToastContainer";
import { ArrowLeft, Upload, Loader2 } from "lucide-react";
import { instructorService } from "../services/instructorService";
import { uploadImageFile } from "../services/uploadService";
import { parseApiError, formatValidationErrors } from "../utils/errorHandler";

// Form field component matching Figma design
const FormField = ({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px]">
      {label} {required && <span className="text-[#FF0000]">*</span>}
    </label>
    {children}
  </div>
);

// Section header with numbered badge matching Figma
const SectionHeader = ({
  number,
  title,
}: {
  number: number;
  title: string;
}) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="w-[30px] h-[30px] bg-[#e07828] rounded-full flex items-center justify-center text-white font-semibold text-[16px]">
      {number}
    </span>
    <h2
      className="text-[20px] font-semibold text-[#102040] tracking-[0.5px]"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {title}
    </h2>
  </div>
);

// Custom input matching Figma: 47px height, #DCE0E5 border, 8px radius
const inputClassName =
  "h-[47px] border-[#DCE0E5] rounded-[8px] bg-white text-[14px] text-[#102040] placeholder:text-[#706F6F]";

export default function AddEditInstructorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [isUploading, setIsUploading] = useState(false);
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState({
    // User Info
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    phoneNumber: "",
    profilePicture: "",
    // Instructor Info
    professionalTitle: "",
    yearsOfExperience: 0,
    biography: "",
    expertiseAreas: "",
    // Social Media
    linkedin: "",
    twitter: "",
    youtube: "",
    website: "",
  });

  const fetchInstructor = useCallback(async () => {
    if (!id) return;
    setIsFetching(true);
    try {
      const response = await instructorService.getInstructorById(id);
      const instructor = response.instructor;
      setFormData({
        firstName: instructor.user?.firstName || "",
        lastName: instructor.user?.lastName || "",
        username: instructor.user?.username || "",
        email: instructor.user?.email || "",
        password: "",
        phoneNumber: "",
        profilePicture: instructor.user?.profilePicture || "",
        professionalTitle: instructor.professionalTitle || "",
        yearsOfExperience: instructor.yearsOfExperience || 0,
        biography: instructor.biography || "",
        expertiseAreas: instructor.expertiseAreas?.join(", ") || "",
        linkedin: (instructor.socialMediaLinks as any)?.linkedin || "",
        twitter: (instructor.socialMediaLinks as any)?.twitter || "",
        youtube: (instructor.socialMediaLinks as any)?.youtube || "",
        website: (instructor.socialMediaLinks as any)?.website || "",
      });
    } catch (err: any) {
      console.error("Failed to fetch instructor:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Failed to Load Instructor");
    } finally {
      setIsFetching(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      fetchInstructor();
    }
  }, [isEditMode, fetchInstructor]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const imageUrl = await uploadImageFile(file);
      setFormData((prev) => ({ ...prev, profilePicture: imageUrl }));
    } catch (err: any) {
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Upload Failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    // Basic validation
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.professionalTitle ||
      !formData.biography ||
      !formData.expertiseAreas
    ) {
      setError("Please fill in all required fields (marked with *)");
      setIsLoading(false);
      return;
    }

    if (formData.biography.length < 50) {
      setError("Biography must be at least 50 characters long.");
      setIsLoading(false);
      return;
    }

    try {
      const socialMediaLinks: Record<string, string> = {};
      if (formData.linkedin) socialMediaLinks.linkedin = formData.linkedin;
      if (formData.twitter) socialMediaLinks.twitter = formData.twitter;
      if (formData.youtube) socialMediaLinks.youtube = formData.youtube;
      if (formData.website) socialMediaLinks.website = formData.website;

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username || undefined,
        email: formData.email,
        password: formData.password || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        profilePicture: formData.profilePicture || undefined,
        professionalTitle: formData.professionalTitle,
        yearsOfExperience: Number(formData.yearsOfExperience),
        biography: formData.biography,
        expertiseAreas: formData.expertiseAreas
          .split(",")
          .map((a) => a.trim())
          .filter((a) => a),
        socialMediaLinks: socialMediaLinks,
      };

      if (isEditMode && id) {
        await instructorService.updateInstructor(id, payload);
        showSuccess("Instructor updated successfully", "Success");
      } else {
        await instructorService.createInstructor(payload);
        showSuccess("Instructor created successfully", "Success");
        setTimeout(() => navigate("/instructors"), 1500);
      }
    } catch (err: any) {
      console.error("Submit error:", err);
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(
        apiError.message,
        isEditMode ? "Update Failed" : "Create Failed"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ... (duplicate removal handled previously)

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#FCFCFC] -m-4 p-4 sm:-m-8 sm:p-8">
        {/* Header with back button */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => navigate("/instructors")}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-[#102040]" />
          </button>
          <h1
            className="text-[24px] font-semibold text-[#102040]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {isEditMode ? "Edit Instructor" : "Add Instructor"}
          </h1>
        </div>

        {isFetching ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#e07828]" size={32} />
            <p className="text-[#706F6F] ml-3">Loading instructor data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 max-w-[1063px]">
            {/* ... (Error/Success messages and other sections unchanged) */}

            {/* Section 1 & 2 content is unchanged, just skipping for brevity until Section 3 */}
            {/* Wait, I cannot skip content in replace_file_content or I lose it. 
                I need to match specifically the handleSubmit part and then separately the input modifications.
                I will do this in TWO steps to be safer and avoid massive context replacement.
            */}

            {/* Error Messages */}
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-[8px] text-sm border border-red-100">
                <p className="font-medium">{error}</p>
                {Object.keys(validationErrors).length > 0 && (
                  <ul className="mt-2 ml-4 list-disc space-y-1 text-xs">
                    {formatValidationErrors(validationErrors).map(
                      (err, idx) => (
                        <li key={idx}>
                          <span className="font-medium">{err.field}:</span>{" "}
                          {err.messages.join(", ")}
                        </li>
                      )
                    )}
                  </ul>
                )}
              </div>
            )}

            {/* Section 1: Account Details */}
            <div className="bg-white border border-[#DCE0E5] rounded-[8px] p-4 sm:p-6">
              <SectionHeader number={1} title="Account Details" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <FormField label="First Name" required>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    required
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Last Name" required>
                  <Input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    required
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Email" required>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="admin@example.com"
                    required
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Username">
                  <Input
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="admin@example.com"
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Password">
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••••"
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Phone Number">
                  <Input
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+1 234 567 8900"
                    className={inputClassName}
                  />
                </FormField>
              </div>

              {/* Profile Photo Section - Matching Figma */}
              <div className="mt-6">
                <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-3 block">
                  Profile Photo
                </label>
                <div className="flex items-center gap-6">
                  {/* Photo Circle - Clickable */}
                  <div className="relative">
                    <input
                      type="file"
                      id="profilePictureFile"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="profilePictureFile"
                      className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E2E2E2] bg-[#FCFCFC] flex flex-col items-center justify-center overflow-hidden cursor-pointer hover:border-[#e07828] transition-colors"
                    >
                      {isUploading ? (
                        <Loader2
                          className="animate-spin text-[#e07828]"
                          size={28}
                        />
                      ) : formData.profilePicture ? (
                        <img
                          src={formData.profilePicture}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Upload size={28} className="text-[#706F6F]" />
                      )}
                    </label>
                  </div>
                  {/* Upload Text and URL Input */}
                  <div className="flex-1">
                    <p className="text-[14px] text-[#706F6F] tracking-[0.5px] mb-2">
                      {isUploading
                        ? "Uploading..."
                        : "Click the circle to upload or paste URL below"}
                    </p>
                    <Input
                      name="profilePicture"
                      value={formData.profilePicture}
                      onChange={handleChange}
                      placeholder="Or paste image URL here"
                      className={inputClassName}
                      disabled={isUploading}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Instructor Details */}
            <div className="bg-white border border-[#DCE0E5] rounded-[8px] p-4 sm:p-6">
              <SectionHeader number={2} title="Instructor Details" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <FormField label="Professional Title" required>
                  <Input
                    name="professionalTitle"
                    value={formData.professionalTitle}
                    onChange={handleChange}
                    placeholder="e.g. Senior Project Manager"
                    required
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Years of Experience">
                  <Input
                    type="number"
                    name="yearsOfExperience"
                    value={formData.yearsOfExperience}
                    onChange={handleChange}
                    min={0}
                    max={60}
                    className={inputClassName}
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField label="Expertise Areas (comma separated)" required>
                  <Input
                    name="expertiseAreas"
                    value={formData.expertiseAreas}
                    onChange={handleChange}
                    placeholder="Agile, PMP, Risk Management, Leadership"
                    required
                    className={inputClassName}
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <label className="text-[14px] font-normal text-[#102040] tracking-[0.5px] mb-2 block">
                  Biography <span className="text-[#FF0000]">*</span>{" "}
                  <span className="text-[12px] text-[#706F6F]">
                    (Min 50 characters)
                  </span>
                </label>
                <textarea
                  name="biography"
                  value={formData.biography}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 bg-white border border-[#DCE0E5] rounded-[8px] text-[14px] text-[#102040] placeholder:text-[#706F6F] focus:outline-none focus:ring-2 focus:ring-[#e07828]/20 focus:border-[#e07828] transition-all resize-none"
                  placeholder="Write a detailed biography about the instructor..."
                  required
                  minLength={50}
                />
                <p className="text-[12px] text-[#706F6F] mt-1">
                  {formData.biography.length} / 50+ characters
                </p>
              </div>
            </div>

            {/* Section 3: Social Media */}
            <div className="bg-white border border-[#DCE0E5] rounded-[8px] p-4 sm:p-6">
              <SectionHeader number={3} title="Social Media Links" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <FormField label="LinkedIn">
                  <Input
                    type="url"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/username"
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Twitter / X">
                  <Input
                    type="url"
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleChange}
                    placeholder="https://twitter.com/username"
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="YouTube">
                  <Input
                    type="url"
                    name="youtube"
                    value={formData.youtube}
                    onChange={handleChange}
                    placeholder="https://youtube.com/@channel"
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Website">
                  <Input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    className={inputClassName}
                  />
                </FormField>
              </div>
            </div>

            {/* Action Buttons - Matching Figma */}
            <div className="flex flex-col sm:flex-row justify-end gap-4 pt-2 pb-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/instructors")}
                className="h-[47px] w-full sm:w-auto px-8 border-[#706F6F] text-[#706F6F] rounded-[8px] text-[16px]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="h-[47px] w-full sm:w-auto px-8 bg-[#e07828] hover:bg-[#c99800] text-white rounded-[8px] text-[16px] border-transparent"
              >
                {isLoading && (
                  <Loader2 className="animate-spin mr-2" size={16} />
                )}
                {isEditMode ? "save Instructor" : "save Instructor"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
